import React, { useState, useRef } from 'react';
import { ChevronDown, ChevronRight, Edit3, Save, X, Calculator, AlertCircle } from 'lucide-react';
import { getDatabase, ref, set } from 'firebase/database';
import { Building as BuildingType } from '../../types/protocols';
import { BaseType } from '../../types/Enums';
import { translate } from '../../types/EnumTranslations';
import { ComponentLifetime } from '../Förvaltning/TemplatesEditor/types';
import { BaseComponentImpl } from '../../types/implementations';

interface DepreciationTabProps {
    buildings: Record<string, BuildingType>;
    componentLifetimes: Record<string, ComponentLifetime>;
    activePlans: Record<string, any>;
    onDataRefresh?: () => void;
    expandedBuildings: Set<string>;
    setExpandedBuildings: React.Dispatch<React.SetStateAction<Set<string>>>;
    expandedBaseTypes: Set<string>;
    setExpandedBaseTypes: React.Dispatch<React.SetStateAction<Set<string>>>;
}

interface ComponentDepreciation {
    component: any;
    buildingKey: string;
    buildingName: string;
    installYear?: number;
    expectedLifetime?: number;
    replacementCost?: number;
    depreciationCost?: number;
    missingData: string[];
}

const DepreciationTab: React.FC<DepreciationTabProps> = ({
    buildings,
    componentLifetimes,
    activePlans,
    onDataRefresh,
    expandedBuildings,
    setExpandedBuildings,
    expandedBaseTypes,
    setExpandedBaseTypes
}) => {
    const [editingInstallYear, setEditingInstallYear] = useState<{ buildingKey: string; componentKey: string } | null>(null);
    const [editValue, setEditValue] = useState<string>('');
    const [saving, setSaving] = useState<boolean>(false);
    const scrollPositionRef = useRef<number>(0);

    const db = getDatabase();

    // Get buildings with active maintenance plans
    const buildingsWithActivePlans = Object.keys(activePlans).map(buildingKey => {
        const building = buildings[buildingKey];
        return building ? { key: buildingKey, building } : null;
    }).filter(Boolean) as { key: string; building: BuildingType }[];

    const toggleBuilding = (buildingKey: string) => {
        const newExpanded = new Set(expandedBuildings);
        if (newExpanded.has(buildingKey)) {
            newExpanded.delete(buildingKey);
        } else {
            newExpanded.add(buildingKey);
        }
        setExpandedBuildings(newExpanded);
    };

    const toggleBaseType = (buildingKey: string, baseType: string) => {
        const baseTypeKey = `${buildingKey}-${baseType}`;
        const newExpanded = new Set(expandedBaseTypes);
        if (newExpanded.has(baseTypeKey)) {
            newExpanded.delete(baseTypeKey);
        } else {
            newExpanded.add(baseTypeKey);
        }
        setExpandedBaseTypes(newExpanded);
    };

    const calculateDepreciation = (component: any, buildingKey: string): ComponentDepreciation => {
        const missingData: string[] = [];
        let depreciationCost: number | undefined;

        // Get component lifetime data
        const componentType = component.specificType;
        const lifetime = componentType ? componentLifetimes[componentType] : null;

        // Check for install year
        const installYear = component.installYear?.value;
        if (!installYear) {
            missingData.push('Installationsår');
        }

        // Check for expected lifetime
        const expectedLifetime = lifetime?.expectedLifetimeYears;
        if (!expectedLifetime) {
            missingData.push('Förväntad livslängd');
        }

        // Check for replacement cost
        const replacementCost = lifetime?.replacementCost;
        if (!replacementCost) {
            missingData.push('Ersättningskostnad');
        }

        // Calculate depreciation if we have all required data
        if (installYear && expectedLifetime && replacementCost) {
            const currentYear = new Date().getFullYear();
            const yearsSinceInstall = currentYear - installYear;
            const yearsRemaining = Math.max(0, expectedLifetime - yearsSinceInstall);
            
            console.log('Depreciation calculation:', {
                componentKey: component.key,
                installYear,
                expectedLifetime,
                replacementCost,
                currentYear,
                yearsSinceInstall,
                yearsRemaining
            });
            
            if (yearsRemaining > 0) {
                depreciationCost = replacementCost / yearsRemaining;
                console.log('Calculated depreciation cost:', depreciationCost);
            } else {
                depreciationCost = 0; // Component has reached end of life
                console.log('Component has reached end of life, depreciation cost: 0');
            }
        } else {
            console.log('Missing data for depreciation calculation:', {
                componentKey: component.key,
                installYear,
                expectedLifetime,
                replacementCost,
                missingData
            });
        }

        return {
            component,
            buildingKey,
            buildingName: buildings[buildingKey]?.summary?.name || 'Okänd byggnad',
            installYear,
            expectedLifetime,
            replacementCost,
            depreciationCost,
            missingData
        };
    };

    const groupComponentsByBaseType = (components: any[]) => {
        const grouped: Record<string, any[]> = {};
        
        components.forEach(component => {
            const baseType = component.type;
            if (!grouped[baseType]) {
                grouped[baseType] = [];
            }
            grouped[baseType].push(component);
        });

        return grouped;
    };

    const startEditingInstallYear = (buildingKey: string, componentKey: string, currentValue?: number) => {
        setEditingInstallYear({ buildingKey, componentKey });
        setEditValue(currentValue?.toString() || '');
    };

    const saveInstallYear = async () => {
        if (!editingInstallYear) return;

        setSaving(true);
        try {
            const installYearRef = ref(db, `buildings/${editingInstallYear.buildingKey}/structure/components/${editingInstallYear.componentKey}/installYear`);
            const yearValue = parseInt(editValue, 10);
            
            console.log('Saving install year:', {
                buildingKey: editingInstallYear.buildingKey,
                componentKey: editingInstallYear.componentKey,
                yearValue: yearValue,
                path: `buildings/${editingInstallYear.buildingKey}/structure/components/${editingInstallYear.componentKey}/installYear`
            });
            
            if (isNaN(yearValue) || yearValue < 1900 || yearValue > new Date().getFullYear()) {
                alert('Ange ett giltigt år mellan 1900 och ' + new Date().getFullYear());
                return;
            }

            await set(installYearRef, {
                value: yearValue,
                status: 'confirmed',
                createdBy: 'system', // You might want to get this from user context
                createdWhen: new Date().toISOString()
            });

            console.log('Install year saved successfully');
            setEditingInstallYear(null);
            setEditValue('');
            
            // Save current scroll position
            scrollPositionRef.current = window.scrollY;
            
            // Refresh the data to show the updated install year
            if (onDataRefresh) {
                console.log('Calling data refresh...');
                onDataRefresh();
                
                // Restore scroll position after a short delay to allow for re-render
                setTimeout(() => {
                    window.scrollTo(0, scrollPositionRef.current);
                }, 100);
            }
        } catch (error) {
            console.error('Error saving install year:', error);
            alert('Ett fel uppstod vid sparning av installationsår');
        } finally {
            setSaving(false);
        }
    };

    const cancelEditing = () => {
        setEditingInstallYear(null);
        setEditValue('');
    };

    if (buildingsWithActivePlans.length === 0) {
        return (
            <div className="no-plans-section">
                <div className="alert alert-info">
                    <h4 className="alert-heading">Inga aktiva underhållsplaner</h4>
                    <p className="mb-0">
                        Det finns inga byggnader med aktiva underhållsplaner att visa avskrivningar för.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="depreciation-tab">
            <div className="depreciation-header mb-4">
                <h3>Avskrivningar per byggnad</h3>
                <p className="text-muted">
                    Avskrivningskostnad = Ersättningskostnad ÷ Återstående livslängd
                </p>
            </div>

            <div className="buildings-list">
                {buildingsWithActivePlans.map(({ key: buildingKey, building }) => {
                    const isExpanded = expandedBuildings.has(buildingKey);
                    const components = building.model?.structure?.components || {};
                    const componentList = Object.entries(components).map(([componentKey, component]) => ({
                        ...component,
                        key: componentKey
                    }));

                    const componentDepreciations = componentList.map(component => 
                        calculateDepreciation(component, buildingKey)
                    );

                    const totalDepreciation = componentDepreciations.reduce((sum, dep) => 
                        sum + (dep.depreciationCost || 0), 0
                    );

                    return (
                        <div key={buildingKey} className="building-section mb-3">
                            <div 
                                className="building-header d-flex align-items-center justify-content-between p-3 bg-light rounded cursor-pointer"
                                onClick={() => toggleBuilding(buildingKey)}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="d-flex align-items-center">
                                    {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                                    <h4 className="mb-0 ms-2">{building.summary?.name || 'Okänd byggnad'}</h4>
                                </div>
                                <div className="d-flex align-items-center">
                                    <span className="badge bg-primary me-2">
                                        {componentList.length} komponenter
                                    </span>
                                    <span className="badge bg-success">
                                        <Calculator size={16} className="me-1" />
                                        {Math.round(totalDepreciation).toLocaleString('sv-SE')} kr/år
                                    </span>
                                </div>
                            </div>

                            {isExpanded && (
                                <div className="building-content p-3 border rounded">
                                    {Object.entries(groupComponentsByBaseType(componentList)).map(([baseType, components]) => {
                                        const baseTypeKey = `${buildingKey}-${baseType}`;
                                        const isBaseTypeExpanded = expandedBaseTypes.has(baseTypeKey);
                                        
                                        return (
                                            <div key={baseType} className="base-type-section mb-4">
                                                <div 
                                                    className="base-type-header d-flex align-items-center justify-content-between p-2 bg-light rounded cursor-pointer"
                                                    onClick={() => toggleBaseType(buildingKey, baseType)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <div className="d-flex align-items-center">
                                                        {isBaseTypeExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                                        <h5 className="mb-0 ms-2">
                                                            {translate(baseType as BaseType)}
                                                        </h5>
                                                    </div>
                                                    <span className="badge bg-secondary">
                                                        {components.length} komponenter
                                                    </span>
                                                </div>
                                                
                                                {isBaseTypeExpanded && (
                                                    <div className="components-list mt-3">
                                                {components.map(component => {
                                                    const depreciation = componentDepreciations.find(dep => dep.component.key === component.key);
                                                    if (!depreciation) return null;
                                                    
                                                    const componentImpl = new BaseComponentImpl(component);

                                                    return (
                                                        <div key={component.key} className="component-item p-3 border rounded mb-2">
                                                            <div className="d-flex justify-content-between align-items-start">
                                                                <div className="component-info">
                                                                    <h6 className="mb-1">
                                                                        {componentImpl.displayName(building)}
                                                                    </h6>
                                                                    {component.specificType && (
                                                                        <small className="text-muted">
                                                                            {translate(component.specificType)}
                                                                        </small>
                                                                    )}
                                                                </div>
                                                                
                                                                <div className="component-depreciation text-end">
                                                                    {depreciation.depreciationCost !== undefined ? (
                                                                        <div>
                                                                            <div className="depreciation-cost h5 text-success mb-1">
                                                                                {Math.round(depreciation.depreciationCost).toLocaleString('sv-SE')} kr/år
                                                                            </div>
                                                                            <small className="text-muted">
                                                                                Installerat: {depreciation.installYear} | 
                                                                                Livslängd: {depreciation.expectedLifetime} år | 
                                                                                Ersättning: {Math.round(depreciation.replacementCost || 0).toLocaleString('sv-SE')} kr
                                                                            </small>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="missing-data">
                                                                            <div className="d-flex align-items-center text-warning mb-1">
                                                                                <AlertCircle size={16} className="me-1" />
                                                                                <span>Kan inte beräkna avskrivning</span>
                                                                            </div>
                                                                            <small className="text-muted">
                                                                                Saknar: {depreciation.missingData.join(', ')}
                                                                            </small>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Install Year Edit Section */}
                                                            <div className="install-year-section mt-2 pt-2 border-top">
                                                                <div className="d-flex align-items-center justify-content-between">
                                                                    <div>
                                                                        <small className="text-muted">Installationsår:</small>
                                                                        {editingInstallYear?.buildingKey === buildingKey && editingInstallYear?.componentKey === component.key ? (
                                                                            <div className="d-flex align-items-center mt-1">
                                                                                <input
                                                                                    type="number"
                                                                                    className="form-control form-control-sm me-2"
                                                                                    style={{ width: '100px' }}
                                                                                    value={editValue}
                                                                                    onChange={(e) => setEditValue(e.target.value)}
                                                                                    min="1900"
                                                                                    max={new Date().getFullYear()}
                                                                                />
                                                                                <button
                                                                                    className="btn btn-success btn-sm me-1"
                                                                                    onClick={saveInstallYear}
                                                                                    disabled={saving}
                                                                                >
                                                                                    <Save size={14} />
                                                                                </button>
                                                                                <button
                                                                                    className="btn btn-secondary btn-sm"
                                                                                    onClick={cancelEditing}
                                                                                    disabled={saving}
                                                                                >
                                                                                    <X size={14} />
                                                                                </button>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="d-flex align-items-center mt-1">
                                                                                <span className="me-2">
                                                                                    {depreciation.installYear || 'Ej angivet'}
                                                                                </span>
                                                                                <button
                                                                                    className="btn btn-outline-primary btn-sm"
                                                                                    onClick={() => startEditingInstallYear(buildingKey, component.key, depreciation.installYear)}
                                                                                >
                                                                                    <Edit3 size={14} />
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default DepreciationTab;
